import React, { useState } from "react";
import { useSession } from "../lib/session";
import { supabase } from "../lib/supabase";
import { Link } from "react-router-dom";
import { Field, Notice, PageHead } from "../components/ui";
import Back from "../components/Back";
import { initTheme, setTheme } from "../lib/theme";

const MIN = 8;

/**
 * Your account. Mainly here so a member can change their password
 * without having to pretend they've forgotten it.
 */
export default function Account() {
  const { user, profile, societies, society, isPlatformAdmin,
          leaveSociety, signOut } = useSession();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState("");
  const [mode, setMode] = useState(() => initTheme());

  async function save(e) {
    e.preventDefault();
    setError(""); setDone("");
    if (password.length < MIN) return setError(`Use at least ${MIN} characters.`);
    if (password !== confirm) return setError("Those passwords don't match.");
    setBusy(true);
    const { error } = await supabase.auth.updateUser({ password });
    setBusy(false);
    if (error) return setError(error.message);
    setPassword(""); setConfirm("");
    setDone("Password changed. You'll use the new one next time you sign in.");
  }

  return (
    <>
      {/* reached from More inside a society, or from the welcome screen,
          which has no shell to go back into */}
      <Back to="/">{society ? society.name : "Your societies"}</Back>
      <PageHead title="Your account" sub={profile?.full_name} />

      <div className="surface" style={{ padding: "1rem 1.1rem", marginBottom: "1.25rem" }}>
        <div className="row" style={{ padding: ".3rem 0" }}>
          <span className="muted" style={{ fontSize: ".8125rem" }}>Email</span>
          <span className="spacer" />
          <strong style={{ fontSize: ".9375rem" }}>{user?.email}</strong>
        </div>
        <div className="row" style={{ padding: ".3rem 0" }}>
          <span className="muted" style={{ fontSize: ".8125rem" }}>Handicap index</span>
          <span className="spacer" />
          <strong style={{ fontSize: ".9375rem" }}>{profile?.handicap_index ?? "not set"}</strong>
        </div>
        <div style={{ padding: ".45rem 0 .2rem" }}>
          <span className="muted" style={{ fontSize: ".8125rem" }}>Societies</span>
          <div style={{ marginTop: ".35rem", display: "flex", flexDirection: "column", gap: ".3rem" }}>
            {societies.length === 0 && <strong style={{ fontSize: ".9375rem" }}>none</strong>}
            {societies.map((s) => (
              <div className="row" key={s.id}>
                <strong style={{ fontSize: ".9375rem" }}>{s.name}</strong>
                <span className="spacer" />
                <span className={`pill ${s.role === "player" ? "pill-quiet" : "pill-mark"}`}>
                  {s.role}
                </span>
              </div>
            ))}
            {isPlatformAdmin && (
              <div className="row" style={{ marginTop: ".25rem" }}>
                <strong style={{ fontSize: ".9375rem" }}>Whole site</strong>
                <span className="spacer" />
                <span className="pill pill-live">platform admin</span>
              </div>
            )}
          </div>
        </div>
        <p className="muted" style={{ fontSize: ".75rem", marginTop: ".7rem", lineHeight: 1.6 }}>
          Your handicap index is set by your organiser, so it matches the one
          used for everyone's playing handicaps. <strong>Player</strong> enters
          their own card; <strong>organiser</strong> runs the golf;
          <strong> admin</strong> also decides who is what.
        </p>
      </div>

      {/* switching society and joining another are yours, not the
          society's — they belong with your account, not its settings */}
      <div className="menu" style={{ marginBottom: "1.25rem" }}>
        {society && (
          <button className="menu-row" onClick={leaveSociety}>
            <span>
              <strong>{societies.length > 1 ? "Switch society" : "Your societies"}</strong>
              <small>
                {societies.length > 1 ? `You're in ${societies.length}`
                                      : "Back to the welcome screen"}
              </small>
            </span>
          </button>
        )}
        <Link to="/join" className="menu-row">
          <span>
            <strong>Join or start another society</strong>
            <small>With a code from its organiser</small>
          </span>
        </Link>
      </div>

      <form className="surface" style={{ padding: "1.1rem", maxWidth: 440 }} onSubmit={save}>
        <h2 style={{ fontSize: "1rem", marginBottom: ".8rem" }}>Change password</h2>
        {/* so the keychain updates the existing entry rather than adding one */}
        <input type="email" value={user?.email ?? ""} readOnly autoComplete="username"
               name="username" tabIndex={-1} aria-hidden="true" className="offscreen" />
        <Field label="New password" hint={`At least ${MIN} characters.`}>
          <input className="input" type="password" required minLength={MIN}
                 autoComplete="new-password" value={password}
                 onChange={(e) => setPassword(e.target.value)} />
        </Field>
        <Field label="Type it again">
          <input className="input" type="password" required minLength={MIN}
                 autoComplete="new-password" value={confirm}
                 onChange={(e) => setConfirm(e.target.value)} />
        </Field>
        <Notice kind="error">{error}</Notice>
        <Notice kind="ok">{done}</Notice>
        <button className="btn btn-primary" disabled={busy} style={{ marginTop: ".4rem" }}>
          {busy ? "Saving…" : "Change password"}
        </button>
      </form>

      <div className="surface" style={{ padding: "1rem 1.1rem", marginTop: "1.25rem", maxWidth: 440 }}>
        <div className="row">
          <div>
            <strong style={{ fontSize: ".9375rem" }}>Appearance</strong>
            <div className="muted" style={{ fontSize: ".75rem", marginTop: ".15rem" }}>
              Dark is easier on the eyes in the bar; light reads better outside.
            </div>
          </div>
          <span className="spacer" />
          <div style={{ display: "flex", gap: ".3rem" }}>
            {["light", "dark"].map((m) => (
              <button key={m} type="button" onClick={() => setMode(setTheme(m))}
                      className={`btn ${mode === m ? "btn-dark" : "btn-ghost"}`}
                      style={{ padding: ".45rem .8rem", fontSize: ".8125rem" }}>
                {m === "light" ? "Light" : "Dark"}
              </button>
            ))}
          </div>
        </div>
      </div>

      <button className="btn btn-ghost" onClick={signOut} style={{ marginTop: "1.25rem" }}>
        Sign out
      </button>
    </>
  );
}
